import { useAuth } from "../../auth/AuthContext";
import { useI18n, type MsgKey } from "../../i18n";
import {
  OPERATIONS_NODES,
  REVIEW_NODES,
  SCOPE_NODE,
  STANDARD_SOURCE_NODES,
  type GuideNode,
} from "./guideData";

const ROW_H = 46;
const ROOT_W = 152;
const HUB_W = 120;
const LEAF_W = 214;
const X_ROOT = 0;
const X_HUB = 196;
const X_LEAF = 364;
const CANVAS_W = X_LEAF + LEAF_W;

const BRANCHES: { titleKey: MsgKey; nodes: GuideNode[] }[] = [
  { titleKey: "sysGuideStageStandards", nodes: [...STANDARD_SOURCE_NODES, SCOPE_NODE] },
  { titleKey: "sysGuideStageReview", nodes: REVIEW_NODES },
  { titleKey: "sysGuideStageOperations", nodes: OPERATIONS_NODES },
];

/** A bezier that leaves its origin horizontally, the way mind-map branches read. */
function branchPath(x1: number, y1: number, x2: number, y2: number) {
  const bend = Math.max(24, (x2 - x1) / 2);
  return `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`;
}

export function SystemGuideMindMap({ onSelect }: { onSelect: (node: GuideNode) => void }) {
  const { t } = useI18n();
  const { can } = useAuth();

  let row = 0;
  const branches = BRANCHES.map(({ titleKey, nodes }) => {
    const leaves = nodes.map((node) => ({ node, y: row++ * ROW_H + ROW_H / 2 }));
    const ys = leaves.map((leaf) => leaf.y);
    const hubY = (Math.min(...ys) + Math.max(...ys)) / 2;
    return { titleKey, leaves, hubY };
  });
  const canvasH = row * ROW_H;
  const rootY = canvasH / 2;

  return (
    <div className="overflow-x-auto">
      <div className="relative mx-auto" style={{ width: CANVAS_W, height: canvasH }}>
        <svg
          className="absolute inset-0"
          width={CANVAS_W}
          height={canvasH}
          aria-hidden="true"
          fill="none"
        >
          {branches.map((branch) => (
            <g key={branch.titleKey}>
              <path
                d={branchPath(ROOT_W, rootY, X_HUB, branch.hubY)}
                stroke="#cbd5e1"
                strokeWidth={1.5}
              />
              {branch.leaves.map(({ node, y }) => (
                <path
                  key={node.id}
                  d={branchPath(X_HUB + HUB_W, branch.hubY, X_LEAF, y)}
                  stroke="#cbd5e1"
                  strokeWidth={1.5}
                />
              ))}
            </g>
          ))}
        </svg>

        <div
          className="absolute flex items-center justify-center rounded-lg bg-brand-600 px-2 text-center text-xs font-bold leading-tight text-white shadow-sm"
          style={{ left: X_ROOT, top: rootY - 24, width: ROOT_W, height: 48 }}
        >
          {t("appName")}
        </div>

        {branches.map((branch) => (
          <div key={branch.titleKey}>
            <div
              className="absolute flex items-center justify-center rounded-md border border-brand-200 bg-brand-50 px-2 text-center text-xs font-bold leading-tight text-brand-700"
              style={{ left: X_HUB, top: branch.hubY - 19, width: HUB_W, height: 38 }}
            >
              {t(branch.titleKey)}
            </div>
            {branch.leaves.map(({ node, y }) => (
              <LeafNode
                key={node.id}
                node={node}
                top={y - 17}
                locked={Boolean(node.perm && !can(node.perm))}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeafNode({
  node,
  top,
  locked,
  onSelect,
}: {
  node: GuideNode;
  top: number;
  locked: boolean;
  onSelect: (node: GuideNode) => void;
}) {
  const { t } = useI18n();
  const style = { left: X_LEAF, top, width: LEAF_W, height: 34 };
  const base =
    "absolute flex items-center rounded-md border px-3 text-left text-xs font-semibold";

  if (node.to === null) {
    return (
      <div
        className={`${base} border-dashed border-slate-300 bg-slate-50 text-slate-600`}
        style={style}
      >
        {t(node.labelKey)}
      </div>
    );
  }

  if (locked) {
    return (
      <div
        className={`${base} border-slate-200 bg-white text-slate-400`}
        style={style}
        title={t("sysGuideLockedHint")}
      >
        <span className="truncate">{t(node.labelKey)}</span>
        <span className="ml-auto shrink-0 pl-2 text-[10px] font-medium text-slate-400">
          {t("sysGuideLocked")}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(node)}
      title={t("sysGuideStartTour")}
      className={`${base} border-slate-300 bg-white text-slate-700 transition hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}
      style={style}
    >
      <span className="truncate">{t(node.labelKey)}</span>
      <span aria-hidden="true" className="ml-auto pl-2 text-slate-400">
        ›
      </span>
    </button>
  );
}
